import { useState } from 'react';
import { Button } from '@/components/ui/button-comp';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getCookie } from '@ekonavi/auth';
import { CheckCircle, Clock, Eye, Award, Shield, FileText, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiEndpoint } from '@/config';

interface Assignment {
	id: string;
	type: string;
	status: string;
	created_at: string;
	report_id?: string;
	verifier_id?: string;
	valuator_id?: string;
	certifier_id?: string;
	report?: {
		title: string;
		producer?: {
			name: string;
		};
	};
}

interface AssignmentManagementProps {
	assignments: Assignment[];
	onUpdate: () => void;
}

export function AssignmentManagement({ assignments, onUpdate }: AssignmentManagementProps) {
	const authToken = getCookie('auth_token');
	const authHeaders = authToken ? { 'Authorization': `Bearer ${authToken}` } : undefined;
	const [isLoading, setIsLoading] = useState(false);
	const [selectedAssignee, setSelectedAssignee] = useState<{ [key: string]: string }>({});

	const handleAssign = async (assignmentId: string, assignmentType: string, assigneeId: string) => {
		if (!authHeaders || !assigneeId) return;

		setIsLoading(true);
		try {
			let endpoint = '';
			let updateField = '';

			switch (assignmentType) {
				case 'verification':
					endpoint = `/v1/verifications/${assignmentId}`;
					updateField = 'verifier_id';
					break;
				case 'valuation':
					endpoint = `/v1/valuations/${assignmentId}`;
					updateField = 'valuator_id';
					break;
				case 'certification':
					endpoint = `/v1/certificates/${assignmentId}`;
					updateField = 'certifier_id';
					break;
			}

			if (endpoint) {
				const baseUrl = apiEndpoint.replace('/v1', '');
				const response = await fetch(`${baseUrl}${endpoint}`, {
					method: 'PUT',
					headers: {
						...authHeaders,
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						[updateField]: assigneeId,
						status: 'assigned'
					})
				});

				if (response.ok) {
					toast.success(`${getAssignmentLabel(assignmentType)} atribuída com sucesso!`);
					onUpdate();
				} else {
					toast.error('Erro ao atribuir tarefa');
				}
			}
		} catch (error) {
			toast.error('Erro de conexão com a API');
			console.error('Error assigning task:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleReassign = async (assignmentId: string, assignmentType: string) => {
		if (!authHeaders) return;

		setIsLoading(true);
		try {
			let endpoint = '';

			switch (assignmentType) {
				case 'verification':
					endpoint = `/v1/verifications/${assignmentId}`;
					break;
				case 'valuation':
					endpoint = `/v1/valuations/${assignmentId}`;
					break;
				case 'certification':
					endpoint = `/v1/certificates/${assignmentId}`;
					break;
			}

			if (endpoint) {
				const baseUrl = apiEndpoint.replace('/v1', '');
				const response = await fetch(`${baseUrl}${endpoint}`, {
					method: 'PUT',
					headers: {
						...authHeaders,
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({
						status: 'pending'
					})
				});

				if (response.ok) {
					toast.success('Atribuição removida. Tarefa retornou para fila pendente.');
					onUpdate();
				} else {
					toast.error('Erro ao remover atribuição');
				}
			}
		} catch (error) {
			toast.error('Erro de conexão com a API');
			console.error('Error reassigning task:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const getAssignmentIcon = (type: string) => {
		switch (type) {
			case 'verification': return <Eye className="w-4 h-4" />;
			case 'valuation': return <Award className="w-4 h-4" />;
			case 'certification': return <Shield className="w-4 h-4" />;
			default: return <FileText className="w-4 h-4" />;
		}
	};

	const getAssignmentLabel = (type: string) => {
		switch (type) {
			case 'verification': return 'Verificação';
			case 'valuation': return 'Avaliação';
			case 'certification': return 'Certificação';
			default: return type;
		}
	};

	const getStatusBadge = (status: string) => {
		switch (status) {
			case 'pending':
				return <Badge variant="outline" className="text-orange-600 border-orange-300"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
			case 'assigned':
				return <Badge variant="outline" className="text-blue-600 border-blue-300"><Clock className="w-3 h-3 mr-1" />Atribuído</Badge>;
			case 'in_progress':
				return <Badge variant="outline" className="text-orange-600 border-orange-300"><Clock className="w-3 h-3 mr-1" />Em Progresso</Badge>;
			case 'completed':
				return <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Concluído</Badge>;
			case 'issued':
				return <Badge variant="outline" className="text-emerald-600 border-emerald-300"><CheckCircle className="w-3 h-3 mr-1" />Emitido</Badge>;
			default:
				return <Badge variant="outline">{status}</Badge>;
		}
	};

	const canAssign = (assignment: Assignment) => {
		return assignment.status === 'pending' || assignment.status === 'assigned';
	};

	const isAssigned = (assignment: Assignment) => {
		return assignment.verifier_id || assignment.valuator_id || assignment.certifier_id;
	};

	if (assignments.length === 0) {
		return (
			<div className="text-center py-8">
				<FileText className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
				<p className="text-muted-foreground">Nenhuma atribuição encontrada</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Tipo</TableHead>
						<TableHead>Relatório</TableHead>
						<TableHead>Produtor</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Data</TableHead>
						<TableHead>Ações</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{assignments.map((assignment) => (
						<TableRow key={assignment.id}>
							<TableCell>
								<div className="flex items-center gap-2">
									{getAssignmentIcon(assignment.type)}
									{getAssignmentLabel(assignment.type)}
								</div>
							</TableCell>
							<TableCell>
								<div>
									<p className="font-medium">{assignment.report?.title || 'Título não informado'}</p>
									<p className="text-sm text-muted-foreground">ID: {assignment.report_id}</p>
								</div>
							</TableCell>
							<TableCell>
								<div className="flex items-center gap-2">
									<User className="w-4 h-4" />
									{assignment.report?.producer?.name || 'Não informado'}
								</div>
							</TableCell>
							<TableCell>{getStatusBadge(assignment.status)}</TableCell>
							<TableCell>
								{new Date(assignment.created_at).toLocaleDateString('pt-BR')}
							</TableCell>
							<TableCell>
								<div className="flex items-center gap-2">
									{canAssign(assignment) && !isAssigned(assignment) && (
										<div className="flex items-center gap-2">
											<Select
												value={selectedAssignee[assignment.id] || ''}
												onValueChange={(value) => setSelectedAssignee(prev => ({
													...prev,
													[assignment.id]: value
												}))}
											>
												<SelectTrigger className="w-40">
													<SelectValue placeholder="Selecionar..." />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="user1">João Silva</SelectItem>
													<SelectItem value="user2">Maria Santos</SelectItem>
													<SelectItem value="user3">Carlos Oliveira</SelectItem>
												</SelectContent>
											</Select>
											<Button
												size="sm"
												onClick={() => handleAssign(
													assignment.id,
													assignment.type,
													selectedAssignee[assignment.id]
												)}
												disabled={isLoading || !selectedAssignee[assignment.id]}
											>
												Atribuir
											</Button>
										</div>
									)}
									{isAssigned(assignment) && assignment.status !== 'completed' && assignment.status !== 'issued' && (
										<Button
											variant="outline"
											size="sm"
											onClick={() => handleReassign(assignment.id, assignment.type)}
											disabled={isLoading}
										>
											Reatribuir
										</Button>
									)}
									{(assignment.status === 'completed' || assignment.status === 'issued') && (
										<span className="text-sm text-muted-foreground">Finalizado</span>
									)}
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}